/**
 * Notifications API service
 * Handles communication with the backend for notifications
 */

import { apiGet, apiPost, apiPut } from './api';

export interface NotificationResponse {
	id: string;
	userId: string;
	type:
		| 'offer_received'
		| 'offer_accepted'
		| 'offer_rejected'
		| 'new_message'
		| 'deadline_near'
		| 'deadline_overdue'
		| 'plan_published'
		| 'project_status_changed'
		| 'task_assigned'
		| 'invite_received'
		| 'invite_responded'
		| 'direct_plan_created'
		| 'task_issue_reported'
		| 'project_replanned';
	title: string;
	message: string;
	read: boolean;
	readAt?: string;
	relatedId?: string;
	relatedType?: 'plan' | 'offer' | 'project' | 'task' | 'conversation';
	url?: string;
	createdAt: string;
	updatedAt: string;
}

export interface CreateNotificationRequest {
	userId: string;
	type: NotificationResponse['type'];
	title: string;
	message: string;
	relatedId?: string;
	relatedType?: 'plan' | 'offer' | 'project' | 'task' | 'conversation';
	url?: string;
}

/**
 * Get all notifications for a user
 */
export async function getNotifications(userId: string, unreadOnly: boolean = false): Promise<NotificationResponse[]> {
	const query = new URLSearchParams({
		user_id: userId,
		...(unreadOnly && { unread_only: 'true' })
	});
	return apiGet<NotificationResponse[]>(`/notifications?${query}`);
}

/**
 * Get unread count for a user
 */
export async function getUnreadCount(userId: string): Promise<number> {
	const query = new URLSearchParams({
		user_id: userId,
		count: 'true'
	});
	const response = await apiGet<{ count: number }>(`/notifications?${query}`);
	return response.count;
}

/**
 * Create a new notification
 */
export async function createNotification(notification: CreateNotificationRequest): Promise<NotificationResponse> {
	return apiPost<NotificationResponse>('/notifications', notification);
}

/**
 * Mark notification as read
 */
export async function markAsRead(notificationId: string): Promise<void> {
	await apiPut<void>(`/notifications/${notificationId}/read`, {});
}

/**
 * Mark notification as unread
 */
export async function markAsUnread(notificationId: string): Promise<void> {
	await apiPut<void>(`/notifications/${notificationId}/unread`, {});
}

/**
 * Mark all notifications as read for a user
 */
export async function markAllAsRead(userId: string): Promise<void> {
	const query = new URLSearchParams({ user_id: userId });
	await apiPut<void>(`/notifications/read-all?${query}`, {});
}

/**
 * Delete a notification
 */
export async function deleteNotification(notificationId: string): Promise<void> {
	const { apiDelete } = await import('./api');
	await apiDelete<void>(`/notifications/${notificationId}`);
}

